import React, { useEffect, useState } from "react";
import './styles.css';
import UpcomingCards from "../../components/UpcomingCards";
import { Box, Tooltip, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList, faCalendarDays, faDownLeftAndUpRightToCenter, faMaximize } from "@fortawesome/free-solid-svg-icons";
import { colors } from "../../common/pallette/colors";
import Calendar from "../../components/Calendar";
import SortBox from "../../components/SortBox";
import { sortData } from "../../common/SortSupport";
import UpcomingCardsSkeleton from "../../components/UpcomingCardsSkeleton";
import { useDispatch, useSelector } from "react-redux";
import { setDisplayUpcoming } from "../../redux/reducers/common";
import { formatDate } from "../../common/StaticExports";

/** Small round icon toggle button — matches the style of the card/table toggle in MyClubsContainer */
const IconToggleBtn = ({
  icon,
  isActive,
  tooltip,
  onClick,
  size = 26,
}: {
  icon: any;
  isActive: boolean;
  tooltip: string;
  onClick: () => void;
  size?: number;
}) => (
  <Tooltip title={tooltip} arrow placement="top">
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: '50%',
        cursor: 'pointer',
        backgroundColor: isActive ? `${colors.blue.navy}18` : 'transparent',
        transition: 'background 0.15s',
        flexShrink: 0,
        '&:hover': { backgroundColor: `${colors.blue.navy}15` },
      }}
    >
      <FontAwesomeIcon
        icon={icon}
        style={{
          fontSize: '12px',
          color: isActive ? colors.blue.navy : colors.grey.dark,
        }}
      />
    </Box>
  </Tooltip>
);

const EvaluationUpcoming = (props: any) => {
    const { isExpanded, setIsExpanded, disableExpand } = props;
    const [showBy, setShowBy] = React.useState('list');
    const searchString = useSelector((state:any) => state.common.adminPageSearchString);
    const upcomingData = useSelector((state:any) => state.common.upcoming);
    const displayUpcomingData = useSelector((state:any) => state.common.displayUpcoming);
    const [order, setOrder] = React.useState('');
    const [orderBy, setOrderBy] = React.useState('');
    const dispatch = useDispatch();
    const [isFetching, setIsFetching] = useState(false);

    const optionData = [
        {property: 'upcomingDateTime', name: 'Date asc'},
        {property: 'upcomingDateTime', name: 'Date desc'},
        {property: 'clubName', name: 'Club asc'},
        {property: 'clubName', name: 'Club desc'},
    ];

    const handleRequestSort = (value: any) => {
        let property: any = optionData.find((d:any) => d.name == value);
        setOrder(value.includes('asc') ? 'asc' : 'desc');
        setOrderBy(property?.property);
    };

    const updatedData = React.useMemo(
        () => displayUpcomingData?.length > 0 && sortData(displayUpcomingData, orderBy, order),
        [order, orderBy, displayUpcomingData],
    );

    useEffect(() => {
        setIsFetching(true);
        if (searchString !== '') {
            const tempArr = upcomingData.filter((d:any) =>
                d.clubName.toLowerCase().includes(searchString.toLowerCase()) ||
                d.upcomingEvent.toLowerCase().includes(searchString.toLowerCase()) ||
                d.upcomingDateTime.toString().includes(searchString)
            );
            dispatch(setDisplayUpcoming(tempArr));
        } else {
            dispatch(setDisplayUpcoming(upcomingData));
        }
        setIsFetching(false);
    }, [searchString, upcomingData]);


    const resultCount = displayUpcomingData?.length ?? 0;
    const isSearchActive = searchString !== '';

    return (
        <React.Fragment>
            <Box sx={{ bgcolor: colors.grey.bright }} className='upcomingWrapper'>

                {/* ── Header ── */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '4px' }}>

                    {/* Left: title + compact count badge */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0, overflow: 'hidden' }}>
                        <span className="collapseHeading" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>UPCOMING</span>
                        <Box
                            sx={{
                                fontSize: '10px',
                                fontWeight: 700,
                                px: '6px',
                                py: '1px',
                                borderRadius: '20px',
                                backgroundColor: isSearchActive
                                    ? (resultCount > 0 ? `${colors.blue.navy}15` : 'rgba(224,68,68,0.10)')
                                    : `${colors.grey.dark}18`,
                                color: isSearchActive
                                    ? (resultCount > 0 ? colors.blue.navy : '#D94040')
                                    : colors.grey.dark,
                                border: `1px solid ${isSearchActive
                                    ? (resultCount > 0 ? `${colors.blue.navy}30` : 'rgba(217,64,64,0.25)')
                                    : `${colors.grey.dark}30`}`,
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap',
                                flexShrink: 0,
                            }}
                        >
                            {/* Compact: "3 events" at full width, just "3" at narrow */}
                            {resultCount}
                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                {' '}{resultCount === 1 ? 'event' : 'events'}
                            </Box>
                        </Box>
                    </Box>

                    {/* Right: sort + toggle icons — all circles, same size, never squeeze */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '1px', flexShrink: 0, ml: '6px' }}>
                        {showBy === 'list' && (
                            <SortBox label={'Sort By'} options={optionData} handleClick={handleRequestSort}/>
                        )}

                        <IconToggleBtn
                            icon={faCalendarDays}
                            isActive={showBy === 'calendar'}
                            tooltip="Calendar View"
                            onClick={() => setShowBy('calendar')}
                        />
                        <IconToggleBtn
                            icon={faList}
                            isActive={showBy === 'list'}
                            tooltip="List View"
                            onClick={() => setShowBy('list')}
                        />
                        {/* Expand — always visible, right next to list icon */}
                        <Tooltip title={isExpanded ? 'Collapse' : 'Expand'} arrow placement="top">
                            <Box
                                onClick={setIsExpanded}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 26,
                                    height: 26,
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    transition: 'background 0.15s',
                                    '&:hover': { backgroundColor: `${colors.blue.navy}15` },
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={isExpanded ? faDownLeftAndUpRightToCenter : faMaximize}
                                    style={{ fontSize: '11px', color: colors.grey.dark }}
                                />
                            </Box>
                        </Tooltip>
                    </Box>
                </Box>

                {/* ── Body ── */}
                {showBy === 'list' ? (
                    <Box sx={{ marginTop: 1 }} className={'upComingCardsWrapper'}>
                        {!isFetching && updatedData && updatedData.length > 0 ? (
                            updatedData.map((up:any, index:any) => (
                                <UpcomingCards
                                    key={index}
                                    cardMedia={up.clubLogo}
                                    cardHeading={formatDate(up.upcomingDateTime)}
                                    cardComment={up.upcomingEvent}
                                    index={index}
                                />
                            ))
                        ) : isFetching ? (
                            Array.from({length: 8}, (_, i) => <UpcomingCardsSkeleton key={i} />)
                        ) : (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '55%',
                                gap: '10px',
                            }}>
                                <Box sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: '50%',
                                    backgroundColor: isSearchActive ? 'rgba(224,68,68,0.08)' : `${colors.grey.dark}12`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                    <FontAwesomeIcon
                                        icon={faCalendarDays}
                                        style={{ fontSize: '18px', color: isSearchActive ? '#D94040' : colors.grey.dark, opacity: 0.6 }}
                                    />
                                </Box>
                                <Typography sx={{ fontSize: '12px', color: colors.grey.dark, textAlign: 'center', opacity: 0.7, maxWidth: '160px', lineHeight: 1.5 }}>
                                    {isSearchActive
                                        ? <>No upcoming events match<br/><strong>"{searchString}"</strong></>
                                        : 'No upcoming events'
                                    }
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ marginTop: 1 }} className={'upComingCardsWrapper'}>
                        <Calendar
                            data={upcomingData?.map((d: any) => ({
                                title: d.upcomingEvent,
                                allDay: true,
                                date: formatDate(d?.upcomingDateTime),
                                extendedProps: {
                                    clubName: d.clubName,
                                    clubLogo: d.clubLogo,
                                    rawDate: d.upcomingDateTime,
                                },
                            }))}
                            globalSearch={searchString}
                        />
                    </Box>
                )}
            </Box>
        </React.Fragment>
    );
};

export default EvaluationUpcoming;
